/**
 * Global Settings Type Definitions
 * These settings are shared across all accounts and depend on machine/server resources
 */
import type { TastePresetSettings } from './settings'

// Parallel processing settings
export interface ParallelProcessingSettings {
  enabled: boolean // Enable parallel processing (default: true)
  maxConcurrent: number // Maximum concurrent tasks (1-5, default: 4)
}

// API keys (stored server-side, never sent to client in plaintext)
export interface ApiKeySettings {
  openai?: string
  gemini?: string
  elevenlabs?: string
  fal?: string
  xai?: string
  fishaudio?: string
  byteplus?: string
}

// Logging settings
export interface LoggingSettings {
  level: 'debug' | 'info' | 'warn' | 'error'
}

// Cost / financial settings
export interface CostSettings {
  usdToJpy: number // USD to JPY exchange rate (default: 155.91)
  /**
   * Gemini の thinking（思考トークン）。thinking は出力単価で課金されるため既定は 'off'。
   * 'off'  … 無効化してコスト削減（実測で 3.6 Flash は約1/3.4）
   * 'auto' … モデル既定に任せる（品質重視・コスト増）
   * ※ Pro 系は thinking 必須のため設定に関わらず常に thinking する
   */
  geminiThinking: 'off' | 'auto'
}

// Resource limit settings
export interface LimitSettings {
  maxRequestSizeMb: number // Max upload size in MB (default: 50)
  maxConcurrentAnalysis: number // Max concurrent analysis jobs per account (default: 2)
}

// Storage cleanup settings
export interface StorageSettings {
  inactiveProjectAssetTtlDays: number // Days before inactive project assets are auto-deleted (default: 220)
}

// Rendering default settings
export interface RenderingSettings {
  defaultQualityPreset: 'draft' | 'standard' | 'high' | 'ultra'
  defaultAspectRatio: '9:16' | '16:9' | '1:1' | '4:5'
  maxConcurrentRenders: number
  storyboardCrossfadeFrames: number  // StoryboardSlide シーン間クロスフェード長（フレーム数、デフォルト: 6）
  // Lambda オーバーフローレンダリング（ローカル上限超過時のみ発動）
  lambdaEnabled: boolean             // デフォルト: false
  lambdaFunctionName: string         // 例: remotion-render-4-0-259-xxxxx
  lambdaRegion: string               // 例: ap-northeast-1
  lambdaS3BucketName: string         // Remotion Lambda が使う S3 バケット名
  lambdaServeUrl: string             // S3 にデプロイした Remotion サイトの URL
}

// Global settings (machine/server-wide)
export interface GlobalSettings {
  parallelProcessing: ParallelProcessingSettings
  apiKeys: ApiKeySettings
  logging: LoggingSettings
  cost: CostSettings
  limits: LimitSettings
  rendering: RenderingSettings
  storage: StorageSettings
  tastePresetOverrides: Record<string, Partial<TastePresetSettings>>
  /** 管理者がユーザーに見せない TTS ボイスの ID 一覧（VoiceMetadata.id）。AI選択・一覧・試聴から除外される */
  hiddenVoiceIds: string[]
  /** 管理者が無効化した装飾パーツの ID 一覧。装飾ピッカーから除外される（全ユーザーに影響） */
  disabledDecorPartIds: string[]
}

// Default global settings
export const DEFAULT_GLOBAL_SETTINGS: GlobalSettings = {
  parallelProcessing: {
    enabled: true,
    maxConcurrent: 4,
  },
  apiKeys: {},
  logging: {
    level: 'info',
  },
  cost: {
    usdToJpy: 155.91,
    geminiThinking: 'off',
  },
  limits: {
    maxRequestSizeMb: 50,
    maxConcurrentAnalysis: 2,
  },
  rendering: {
    defaultQualityPreset: 'standard',
    defaultAspectRatio: '9:16',
    maxConcurrentRenders: 3,
    storyboardCrossfadeFrames: 0,
    lambdaEnabled: false,
    lambdaFunctionName: '',
    lambdaRegion: 'ap-northeast-1',
    lambdaS3BucketName: '',
    lambdaServeUrl: '',
  },
  storage: {
    inactiveProjectAssetTtlDays: 220,
  },
  tastePresetOverrides: {},
  hiddenVoiceIds: [],
  disabledDecorPartIds: [],
}

// Global settings update request (partial)
export type GlobalSettingsUpdate = Partial<GlobalSettings>

// API key masked response (only last 4 chars shown)
export interface MaskedApiKeys {
  openai: string | null
  gemini: string | null
  elevenlabs: string | null
  fal: string | null
  xai: string | null
  fishaudio: string | null
  byteplus: string | null
}

// Global settings response sent to client (API keys are masked)
export interface GlobalSettingsResponse extends Omit<GlobalSettings, 'apiKeys'> {
  apiKeys: MaskedApiKeys
}
